/**
 * CUSTOMER-R4.15-K13 — first-party UX telemetry context (no PII / secrets).
 * Session-scoped ring of recent actions + last error/load. Never stores initData.
 */

const RING_MAX = 20;
const SECRET_KEY = /(initdata|token|authorization|iban|purpose|card|rnokpp|password|secret|cookie|api[_-]?key|clipboard)/i;
const ORD_RE = /\bord_[a-z0-9-]+\b/gi;

export type ObservabilityAction = {
  at: string;
  name: string;
  route?: string;
};

export type ObservabilitySnapshot = {
  session_id: string;
  navigation_id: string;
  route: string;
  source_route: string | null;
  public_order_number: string | null;
  product_id: string | null;
  category_id: string | null;
  brand_id: string | null;
  country_id: string | null;
  search_query: string | null;
  sort: string | null;
  scroll_y: number | null;
  last_route_load_ms: number | null;
  last_api_error: { code: string | null; path: string | null } | null;
  last_error: { code: string | null; message: string | null } | null;
  recent_actions: ObservabilityAction[];
  correlation_id: string;
  build_id: string | null;
};

let sessionId = newSessionId();
let navigationId = newSessionId();
let correlationId = newSessionId();
let route = "home";
let sourceRoute: string | null = null;
let publicOrderNumber: string | null = null;
let productId: string | null = null;
let categoryId: string | null = null;
let brandId: string | null = null;
let countryId: string | null = null;
let searchQuery: string | null = null;
let sort: string | null = null;
let lastRouteLoadMs: number | null = null;
let lastApiError: ObservabilitySnapshot["last_api_error"] = null;
let lastError: ObservabilitySnapshot["last_error"] = null;
let buildId: string | null = null;
const ring: ObservabilityAction[] = [];
const emittedScrollBuckets = new Set<number>();

function newSessionId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  } catch {
    return `s${Date.now().toString(36)}`;
  }
}

export function sanitizeObservabilityText(raw: unknown, max = 240): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (SECRET_KEY.test(trimmed)) return "[redacted]";
  return trimmed.replace(ORD_RE, "[stg]").slice(0, max);
}

export function resetObservabilitySession(nextBuildId?: string | null): void {
  sessionId = newSessionId();
  navigationId = newSessionId();
  correlationId = newSessionId();
  ring.length = 0;
  emittedScrollBuckets.clear();
  lastRouteLoadMs = null;
  lastApiError = null;
  lastError = null;
  if (nextBuildId) buildId = sanitizeObservabilityText(nextBuildId, 80);
}

export function setObservabilityBuildId(id: string): void {
  buildId = sanitizeObservabilityText(id, 80);
}

export function bumpNavigationId(): void {
  navigationId = newSessionId();
}

export function setObservabilityRoute(next: {
  route: string;
  sourceRoute?: string | null;
  publicOrderNumber?: string | null;
  productId?: string | null;
  categoryId?: string | null;
  brandId?: string | null;
  countryId?: string | null;
  searchQuery?: string | null;
  sort?: string | null;
}): void {
  if (next.route && next.route !== route) {
    sourceRoute = route;
    emittedScrollBuckets.clear();
  }
  route = sanitizeObservabilityText(next.route, 64) ?? "home";
  if (next.sourceRoute != null) sourceRoute = sanitizeObservabilityText(next.sourceRoute, 64);
  publicOrderNumber = sanitizePublicStg(next.publicOrderNumber);
  productId = sanitizeObservabilityText(next.productId, 64);
  categoryId = sanitizeObservabilityText(next.categoryId, 64);
  brandId = sanitizeObservabilityText(next.brandId, 64);
  countryId = sanitizeObservabilityText(next.countryId, 64);
  searchQuery = sanitizeObservabilityText(next.searchQuery, 80);
  sort = sanitizeObservabilityText(next.sort, 32);
}

function sanitizePublicStg(raw: unknown): string | null {
  const text = sanitizeObservabilityText(raw, 32);
  if (!text) return null;
  if (/^STG-?\d+/i.test(text)) return text.toUpperCase().startsWith("STG") ? text : `STG-${text}`;
  if (ORD_RE.test(text) || /^ord_/i.test(text)) return null;
  return text;
}

export function recordObservabilityAction(name: string): void {
  const clean = sanitizeObservabilityText(name, 80);
  if (!clean) return;
  ring.push({ at: new Date().toISOString(), name: clean, route });
  if (ring.length > RING_MAX) ring.shift();
}

export function recordRouteLoadDuration(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  lastRouteLoadMs = Math.min(Math.round(ms), 120_000);
}

export function recordLastApiError(code: string | null, path: string | null): void {
  lastApiError = {
    code: sanitizeObservabilityText(code, 64),
    path: sanitizeApiPath(path),
  };
}

export function recordLastError(code: string | null, message: string | null): void {
  lastError = {
    code: sanitizeObservabilityText(code, 64),
    message: sanitizeObservabilityText(message, 180),
  };
}

function sanitizeApiPath(path: string | null): string | null {
  const raw = sanitizeObservabilityText(path, 120);
  if (!raw) return null;
  return raw.replace(/[?&][^=]*=([^&]+)/g, (m) => {
    if (/token|init|auth|iban|key/i.test(m)) return "[redacted]";
    return m.slice(0, 24);
  });
}

export function noteScrollDepthBucket(pct: number): number | null {
  const bucket = pct >= 100 ? 100 : pct >= 75 ? 75 : pct >= 50 ? 50 : pct >= 25 ? 25 : 0;
  if (bucket === 0 || emittedScrollBuckets.has(bucket)) return null;
  emittedScrollBuckets.add(bucket);
  return bucket;
}

export function getObservabilitySnapshot(): ObservabilitySnapshot {
  let scrollY: number | null = null;
  try {
    const el = document.querySelector("[data-store-scroll-root='true']");
    if (el instanceof HTMLElement) scrollY = Math.round(el.scrollTop);
    else scrollY = Math.round(window.scrollY || 0);
  } catch {
    scrollY = null;
  }
  return {
    session_id: sessionId,
    navigation_id: navigationId,
    route,
    source_route: sourceRoute,
    public_order_number: publicOrderNumber,
    product_id: productId,
    category_id: categoryId,
    brand_id: brandId,
    country_id: countryId,
    search_query: searchQuery,
    sort,
    scroll_y: scrollY,
    last_route_load_ms: lastRouteLoadMs,
    last_api_error: lastApiError,
    last_error: lastError,
    recent_actions: ring.slice(-RING_MAX),
    correlation_id: correlationId,
    build_id: buildId,
  };
}

export function getObservabilitySessionId(): string {
  return sessionId;
}

export function getObservabilityNavigationId(): string {
  return navigationId;
}
